import { useEffect, useRef } from 'react';
import { useAuthContext } from './useAuthContext';
import { JwtClaimVerificationService } from './jwtClaimVerification';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';

/**
 * Hook to periodically verify JWT claims and handle tenant access changes
 * @param intervalMs Interval in milliseconds to check JWT claims (default: 5 minutes)
 */
export const useJwtClaimVerification = (intervalMs: number = 5 * 60 * 1000) => {
  const { isAuthenticated } = useAuthContext();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Initial verification
    const verifyClaims = async () => {
      try {
        await JwtClaimVerificationService.verifyAndHandleMismatches();
      } catch (error) {
        loggerService.log(LoggerLevel.ERROR, 'Error during JWT claim verification', error);
      }
    };

    // Run initial verification immediately
    verifyClaims();

    // Set up periodic verification
    intervalRef.current = setInterval(verifyClaims, intervalMs);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, intervalMs]);

  // Return a function to manually trigger verification
  const triggerVerification = async () => {
    try {
      await JwtClaimVerificationService.verifyAndHandleMismatches();
    } catch (error) {
      loggerService.log(LoggerLevel.ERROR, 'Error during manual JWT claim verification', error);
    }
  };

  return { triggerVerification };
};
